import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { fmtHoras } from "@/lib/format";

export default function CompensacionSaldoPanel({
  modalidad,
  horas,
  fechaDisfruteInicio,
  fechaDisfruteFin,
  usuarioId,
  periodoDesde,
  periodoHasta,
  onCalculo,
}) {
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fechaDisfruteInicio) {
      setCalc(null);
      onCalculo?.(null);
      return;
    }
    if (modalidad === "horas" && (!horas || Number(horas) <= 0)) {
      setCalc(null);
      onCalculo?.(null);
      return;
    }

    const params = {
      modalidad,
      fecha_disfrute_inicio: fechaDisfruteInicio,
      fecha_disfrute_fin: fechaDisfruteFin || fechaDisfruteInicio,
      periodo_desde: periodoDesde,
      periodo_hasta: periodoHasta,
    };
    if (modalidad === "horas") params.horas = Number(horas);
    if (usuarioId) params.usuario_id = usuarioId;

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/compensaciones/calcular", { params });
        setCalc(data);
        onCalculo?.(data);
      } catch {
        setCalc(null);
        onCalculo?.(null);
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [
    modalidad,
    horas,
    fechaDisfruteInicio,
    fechaDisfruteFin,
    usuarioId,
    periodoDesde,
    periodoHasta,
    onCalculo,
  ]);

  if (!calc && !loading) return null;

  return (
    <div className="space-y-2 rounded-xl border border-violet-200 bg-violet-50/60 p-3 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-violet-800">Saldo de horas extra</p>
      {loading && !calc ? (
        <p className="text-xs text-violet-600">Calculando según horario…</p>
      ) : calc ? (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-white px-2 py-1.5">
              <p className="text-[10px] text-zinc-500">Disponible</p>
              <p className="font-semibold text-violet-900">{fmtHoras(calc.disponible_segundos)}</p>
            </div>
            <div className="rounded-lg bg-white px-2 py-1.5">
              <p className="text-[10px] text-zinc-500">Esta selección</p>
              <p className="font-semibold text-violet-900">{calc.horas_propuestas || fmtHoras(calc.segundos_propuestos)}</p>
            </div>
            <div className={`rounded-lg px-2 py-1.5 ${calc.excede ? "bg-red-50" : "bg-white"}`}>
              <p className="text-[10px] text-zinc-500">Quedaría</p>
              <p className={`font-semibold ${calc.excede ? "text-red-700" : "text-emerald-700"}`}>
                {calc.excede ? "—" : (calc.restante_horas || fmtHoras(Math.max(0, calc.restante_segundos)))}
              </p>
            </div>
          </div>
          {calc.excede && (
            <p className="flex items-center gap-1.5 text-xs font-medium text-red-700">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              Superas el saldo disponible. Puedes hacer varias compensaciones parciales hasta agotarlo.
            </p>
          )}
          {calc.detalle_dias?.length > 0 && (
            <div className="max-h-28 overflow-y-auto rounded-lg border border-violet-100 bg-white p-2">
              <p className="mb-1 text-[10px] font-semibold uppercase text-zinc-500">Horas por día (según horario)</p>
              <ul className="space-y-0.5 text-xs">
                {calc.detalle_dias.map((d) => (
                  <li key={d.fecha} className={`flex justify-between ${d.laborable ? "text-zinc-700" : "text-zinc-400"}`}>
                    <span>{d.fecha}</span>
                    <span>{d.laborable ? d.horas : "No laborable"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
